import React from 'react';
import { Field, Icon, Input, Note, Textarea } from '@repo/design-system';
import type { OfferedTwinType } from '@repo/frontend-types';
import { visuallyHidden } from '@repo/ui';

import SummaryCard, { SummaryRows, type SummaryRow } from './SummaryCard';
import type { Identity } from './TwinDetailsWhere';
import { isIconName, nameError, nameForCreate } from './twinCreateForm';
import { descriptionFieldText, descriptionPatch, namePatch } from './twinDetailsForm';
import { useTwinAvailability } from './useTwinAvailability';

/**
 * The summary's **Identity** card (`twin-details-page-summary`, R3; frames 03 and 04): the name,
 * which may change here, and the BID and type, which never do (feature fixed-identity).
 *
 * The name is held to the create rules: checked as it is typed with the twin's own BID excepted
 * (`useTwinAvailability`'s `exceptBid`), and saved on blur, Enter and *Done* through the page's queue
 * when it is not blank, not reported taken, and differs from the saved name after `nameForCreate`
 * (`namePatch`). The server has the last word: a refusal comes back as `refusal` and keeps the card
 * open on the refused name (R3.5).
 *
 * The description (`retire-edit-mode-twin-description`) is optional free text, shown under the name
 * with its line breaks kept. In edit it is a `Textarea` beneath the name, saved on blur and on
 * *Done* when it differs from the saved description once both are trimmed (`descriptionPatch`); a
 * blank one clears it. Enter is a line break, Escape puts the saved description back. *Done* saves
 * the description whatever the name's state, then closes only if the name may close (R5).
 *
 * The draft lives in the step, not here, so the step's *changed but not saved* report covers it.
 */

/** The card's typed values while it is in edit. */
export interface IdentityDraft {
  name: string;
  description: string;
}

/** R3.3: the create form's sentence names *Create Twin*; here the save is what checks again. */
export const NAME_COULD_NOT_CHECK = 'Couldn’t check — saving checks again';

/**
 * R3.6: frame 04 opens with *Renaming carries to BI on the next launch.*, and that sentence ships
 * only once it is verified on staging. It could not be: BI Staging reads its twin names from AdminDB
 * PRODUCTION (`private.fdw_twin` on `admin_db_srv`, read 2026-10-26), so no staging rename can ever
 * reach it. The code trace says a launch's BI step does refresh the name (`import_spatial_hierarchy`
 * runs `refresh_mv_dim_twin`, which reads `twin.name`), but the spec's rule is the second sentence
 * alone until it is verified. The query and its answer are in docs/specs/review-twin-details-page-summary.md.
 */
export const RENAME_NOTE = 'The BID stays the same.';

interface Props {
  identity: Identity;
  offered: OfferedTwinType[] | null;
  /** The name the twin has, as far as the step knows: a queued rename, or the saved one. */
  name: string;
  /** The description the twin has, as far as the step knows; `null` or blank reads *Not set*. */
  description: string | null;
  /** For the availability check; `null` when the record names no organisation (nothing is asked). */
  organisationId: number | null;
  getToken: () => Promise<string>;
  /** `null`: the card reads. Otherwise the card is in edit, and these are the typed values. */
  draft: IdentityDraft | null;
  onDraft: (draft: IdentityDraft | null) => void;
  /** Queue the rename. Called only with a name `namePatch` would send. */
  onRename: (name: string) => void;
  /** Queue the description. Called only with text `descriptionPatch` would send. */
  onDescribe: (typed: string) => void;
  /** The server's refusal of the name still in the field, if any. */
  refusal: string | null;
}

const SummaryIdentityCard: React.FC<Props> = ({
  identity,
  offered,
  name,
  description,
  organisationId,
  getToken,
  draft,
  onDraft,
  onRename,
  onDescribe,
  refusal,
}) => {
  const type = offered?.find(t => t.id === identity.twin_template_id) ?? null;
  const typeName = type?.name ?? (identity.template?.trim() || null);
  // No disc when the type is unknown or the list did not load (Failure States, as `IdentityLine`).
  const glyph = type && isIconName(type.icon) ? type.icon : undefined;

  const editing = draft !== null;
  const describedText = descriptionFieldText(description);
  const wanted = draft === null ? null : namePatch(draft.name, name);
  const blank = draft !== null && nameForCreate(draft.name) === '';
  const availability = useTwinAvailability({
    getToken,
    organisationId: organisationId ?? 0,
    bid: '',
    // Only a changed name is asked about: the twin's own name is never "taken".
    name: wanted?.name && organisationId !== null ? wanted.name : '',
    exceptBid: identity.bid,
  });
  const check = wanted ? availability.name : 'idle';
  const checkError = wanted ? nameError(check) : null;
  const error = blank ? 'Enter a name' : (checkError ?? refusal);
  const aside =
    check === 'pending'
      ? 'Checking…'
      : typeof check === 'object' && check.answer === undefined
        ? NAME_COULD_NOT_CHECK
        : undefined;

  /** Save the typed name if it may be sent. `false`: it may not (blank, taken, refused). */
  const trySave = (): boolean => {
    if (draft === null) return true;
    if (blank || checkError || refusal) return false;
    if (wanted?.name) onRename(wanted.name);
    return true;
  };

  /** Save the typed description if it changed (R3). Never blocked: the route refuses no text. */
  const saveDescription = () => {
    if (draft !== null && descriptionPatch(draft.description, description)) {
      onDescribe(draft.description);
    }
  };

  // Said, not only shown (criterion 12): a live region announces each change of its text.
  const said = error ? `Name: ${error}` : aside === NAME_COULD_NOT_CHECK ? aside : '';

  const fixedRows: SummaryRow[] = [
    {
      key: 'bid',
      label: 'BID',
      value: identity.bid,
      mono: true,
      tag: { tone: 'neutral', text: 'Fixed', lock: true, label: 'BID, can’t change' },
    },
    {
      key: 'type',
      label: 'Type',
      value: typeName ?? 'Not set',
      tag: { tone: 'neutral', text: 'Fixed', lock: true, label: 'Type, can’t change' },
    },
  ];

  return (
    <SummaryCard
      surface="on-scene"
      title="Identity"
      glyph={glyph}
      discTone="aqua"
      editing={editing}
      onEdit={() => onDraft({ name, description: describedText })}
      onDone={() => {
        // R5: the description saves whatever the name's state; the name decides whether it closes.
        saveDescription();
        if (trySave()) onDraft(null);
      }}
    >
      {draft !== null ? (
        <>
          <Field label="Name" error={error ?? undefined} aside={error ? undefined : aside}>
            {wiring => (
              <Input
                {...wiring}
                value={draft.name}
                onChange={event => onDraft({ ...draft, name: event.target.value })}
                onBlur={() => {
                  trySave();
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    trySave();
                  } else if (event.key === 'Escape') {
                    event.preventDefault();
                    onDraft({ ...draft, name });
                  }
                }}
              />
            )}
          </Field>
          <Field label="Description">
            {wiring => (
              <Textarea
                {...wiring}
                rows={3}
                value={draft.description}
                onChange={event => onDraft({ ...draft, description: event.target.value })}
                onBlur={saveDescription}
                onKeyDown={event => {
                  // R4: Enter is a line break (not handled); Escape puts the saved text back.
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    onDraft({ ...draft, description: describedText });
                  }
                }}
              />
            )}
          </Field>
        </>
      ) : (
        <SummaryRows
          rows={[
            {
              key: 'name',
              label: 'Name',
              value: name.trim() || 'Not set',
              tag: { tone: 'mint', text: 'Editable' },
            },
            {
              key: 'description',
              label: 'Description',
              value: description?.trim() || 'Not set',
              multiline: true,
              tag: { tone: 'mint', text: 'Editable' },
            },
          ]}
        />
      )}
      <SummaryRows rows={fixedRows} />
      {editing && (
        <Note tone="peri" icon={<Icon name="circle-info" size="xs" />}>
          {RENAME_NOTE}
        </Note>
      )}
      <span role="status" aria-live="polite" style={visuallyHidden}>
        {said}
      </span>
    </SummaryCard>
  );
};

export default SummaryIdentityCard;
